import { GraphQLClient } from 'graphql-request'
import { createRootLogger } from 'logger'
import type { PurposityEnv } from 'purposity-env'

import { getHeaders } from './headers'

type GraphQLClientParams = ConstructorParameters<typeof GraphQLClient>
type GraphQLRequestOptions = GraphQLClientParams[1]

export interface Config {
  /**
   * name of the app where this client is instantiated
   * @example: `backend-sdk` or `sdk`
   * */
  appName: string

  /**
   * name of the tool/package using this client
   * @example: `backend-sdk` or `sdk`
   * */
  identifier: string
  /**
   * URL object created from the API origin
   * @example new URL(`https://funky-engine-31.hasura.app`)
   **/
  origin: URL
  /**
   * Use to override the default endpoint
   * @override
   * @default `/v1/graphql`
   **/
  endpoint?: string
  clientOptions?: GraphQLRequestOptions
  purposityEnv: PurposityEnv
}

export function createClient(
  config: Config
): [client: GraphQLClient, _headers: Record<string, string>] {
  runEdgeRuntimeSanityCheck(config)

  const endpoint = config.endpoint ?? '/v1/graphql'

  const configHeaders =
    typeof config.clientOptions?.headers === 'function'
      ? config.clientOptions?.headers()
      : config.clientOptions?.headers

  const headers = getHeaders(
    {
      appInit: config.identifier,
      clientName: config.identifier,
      purposityEnv: config.purposityEnv,
    },
    Array.isArray(configHeaders)
      ? (configHeaders as [string, string][])
      : configHeaders
  )
  return [
    new GraphQLClient(new URL(endpoint, config.origin).href, {
      ...config.clientOptions,
      headers,
    }),
    headers,
  ]
}

export interface SuperAdminConfig extends Config {
  /**
   * @property `x-hasura-admin-secret` header value
   **/
  secret: string
  /**
   * origin of the URL where the client is running
   * `URL['origin']` *(i.e., `protocol`+`hostname`+`port`; do not include slash or path)*
   * @example `https://www.purposity.com`
   * @example `http://localhost:3000`
   */
  clientOrigin: string
}
export const createSuperAdminClient = (config: SuperAdminConfig) =>
  createClient({
    ...config,
    identifier: `super-admin_${config.identifier}`,
    clientOptions: {
      ...config.clientOptions,
      headers: {
        ...config.clientOptions?.headers,
        'x-hasura-admin-secret': config.secret,
        'x-gateway-action-origin': config.clientOrigin,
      },
    },
  })

/** EdgeRuntime sanity check */
function runEdgeRuntimeSanityCheck(config: Config) {
  const isEdge = typeof EdgeRuntime === 'string'
  const hasFetch = typeof config.clientOptions?.fetch === 'function'

  const log = createRootLogger(
    {},
    {
      severity: 'info',
    }
  ).extend('api-client:fetch-check')
  if (hasFetch) {
    if (isEdge) {
      log.debug({ status: '🟢', EdgeRuntime: true, fetch: true })
    } else {
      log.debug({ status: '🔵', EdgeRuntime: false, fetch: true })
    }
  } else {
    if (isEdge) {
      const stack = new Error().stack
      log.warn(
        'A client was created in the EdgeRuntime without configuring fetch. This will likely lead to errors.',
        {
          status: '🟠',
          EdgeRuntime: true,
          fetch: false,
          clienOptions: config.clientOptions,
          stack,
        }
      )
    } else {
      log.debug({ status: '⚪', EdgeRuntime: false, fetch: false })
    }
  }
}
